import React from 'react';
import { useTheme } from '@emotion/react';
import { Box, Typography } from '@mui/material';
import { WarningAmberRounded } from '@mui/icons-material';

/**
 * A titled block of the editor.
 *
 * The header is separated from the fields by a rule rather than by spacing
 * alone: these pages stack five or six of these, and without the rule the
 * description of one section reads as the first line of the previous one's
 * content. `tone="danger"` is for sections whose changes reach customers
 * differently from the rest — currently the billing rules.
 */
const Section = ({ title, description, action, children, tone }) => {
    const theme = useTheme();
    const danger = tone === 'danger';

    const titleColor = danger ? theme.palette.customColors.r500 : theme.palette.customColors.g300;
    const bodyColor = danger ? theme.palette.customColors.r400 : theme.palette.customColors.g200;

    return (
        <Box sx={{
            backgroundColor: danger ? theme.palette.customColors.r50 : theme.palette.customColors.whiteBG,
            border: `1px solid ${danger ? theme.palette.customColors.r75 : theme.palette.customColors.b50}`,
            borderRadius: '8px',
        }}>
            <Box sx={{
                display: 'flex',
                alignItems: 'flex-start',
                justifyContent: 'space-between',
                gap: '1rem',
                padding: '1rem 1.25rem',
                borderBottom: `1px solid ${danger ? theme.palette.customColors.r75 : theme.palette.customColors.b50}`,
            }}>
                <Box>
                    <Box display="flex" alignItems="center" gap={0.75}>
                        {danger && <WarningAmberRounded fontSize="small" sx={{ color: theme.palette.customColors.r400 }} />}
                        <Typography sx={{ fontSize: '0.9375rem', fontWeight: 700, color: titleColor, lineHeight: 1.4 }}>
                            {title}
                        </Typography>
                    </Box>
                    {description && (
                        <Typography sx={{
                            fontSize: '0.8125rem',
                            color: bodyColor,
                            lineHeight: 1.5,
                            marginTop: '0.125rem',
                            // A measure, not a fixed width: long explanations still
                            // wrap before they get hard to track back from, but a
                            // one-sentence description uses the room it has rather
                            // than breaking with half the card empty beside it.
                            maxWidth: '90ch',
                        }}>
                            {description}
                        </Typography>
                    )}
                </Box>
                {action}
            </Box>

            <Box sx={{ padding: '1.25rem' }}>
                {children}
            </Box>
        </Box>
    );
};

export default Section;
